import { useState, useEffect } from 'react';
import { fetchArtistProfile, fetchArtistEvents } from '@/features/artists/services';
import { useLoadingContext } from '@/hooks/useLoadingContext';
import type { ArtistProfile, ArtistEventData } from '@/features/artists/types/types';

interface UseArtistDataReturn {
  artist: ArtistProfile | null;
  events: ArtistEventData[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch artist profile and events data
 * @param artistId - The unique identifier for the artist
 * @returns Object containing artist data, events, loading state, and error
 */
export const useArtistData = (artistId: string): UseArtistDataReturn => {
  const [artist, setArtist] = useState<ArtistProfile | null>(null);
  const [events, setEvents] = useState<ArtistEventData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { withLoading } = useLoadingContext();

  useEffect(() => {
    const loadArtistData = async () => {
      if (!artistId) return;

      try {
        setLoading(true);
        setError(null);

        // Use global loading system
        await withLoading(async () => {
          // Fetch artist profile and events in parallel
          const [artistResponse, eventsResponse] = await Promise.all([
            fetchArtistProfile(artistId),
            fetchArtistEvents(artistId)
          ]);

          setArtist(artistResponse.data.artist);
          setEvents(eventsResponse.data);
        }, 'artist-data');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load artist data';
        setError(errorMessage);
        console.error('Error loading artist data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadArtistData();
  }, [artistId, withLoading]);

  return { artist, events, loading, error };
};
